import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';
import { useCurrentFarm } from './useCurrentFarm';
import type { ActiveMembership, UserRole } from '@/types/membership';

/**
 * API response from /users/me/membership
 */
interface MembershipApiResponse {
  role: UserRole;
  farm_id: string;
  farm_name: string;
  assigned_ponds: string[];
  default_route: string;
  entry_window_days: number;
  members_edit_applications?: boolean;
}

/**
 * Fetches user membership data from API
 */
async function fetchMembership(farmId?: string | null): Promise<MembershipApiResponse> {
  const url = farmId
    ? `/api/v1/users/me/membership?farm_id=${farmId}`
    : '/api/v1/users/me/membership';
  const response = await api.get<MembershipApiResponse>(url);
  return response;
}

/**
 * Hook to access current user's membership and role
 */
export function useMembership() {
  const { user, session } = useAuth();
  // The reconciled farm, not the raw persisted id: a stale id would 403 here and leave membership null.
  const { farmId, isLoading: farmLoading } = useCurrentFarm();
  // Operator status is a GLOBAL flag from the user profile, not derived from a
  // farm_users membership — so a no-farm operator still counts as an operator
  // (needed for first-farm creation during onboarding).
  const { isOperator: isGlobalOperator } = useProfile();

  const { data: membershipData, isLoading, error } = useQuery({
    queryKey: ['membership', user?.id, farmId],
    queryFn: () => fetchMembership(farmId),
    enabled: !!session && !farmLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    // Never show the previous farm's role/assignments while the new farm's membership loads.
    placeholderData: undefined,
  });

  // Build active membership from API response
  const activeMembership = useMemo((): ActiveMembership | null => {
    if (!membershipData) return null;

    const role = membershipData.role;

    return {
      farmId: membershipData.farm_id,
      farmName: membershipData.farm_name,
      role,
      assignedPondIds: membershipData.assigned_ponds || [],
      entryWindowDays: membershipData.entry_window_days ?? 0,
      membersEditApplications: membershipData.members_edit_applications ?? false,
      canApprove: role === 'supervisor' || role === 'owner' || role === 'operator',
      canManage: role === 'owner' || role === 'operator',
    };
  }, [membershipData]);

  return {
    membership: activeMembership,
    isLoading: isLoading || (!!session && farmLoading),
    error,
    /** User's role for the active farm */
    role: activeMembership?.role ?? null,
    /** Whether user is a technician */
    isTechnician: activeMembership?.role === 'technician',
    /** Whether user is supervisor or owner */
    isSupervisorOrAbove: activeMembership?.role === 'supervisor' || activeMembership?.role === 'owner' || activeMembership?.role === 'operator',
    /** Whether user is farm owner */
    isOwner: activeMembership?.role === 'owner',
    /** Whether user is operator (super-admin) — global flag, farm-independent */
    isOperator: isGlobalOperator || activeMembership?.role === 'operator',
    /** Whether user is owner or operator */
    isOwnerOrAbove: activeMembership?.role === 'owner' || activeMembership?.role === 'operator',
  };
}

/**
 * Role display label. The farm's own titles (Dan D12); role keys and
 * permissions are unchanged.
 */
export function getRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    technician: 'Lab Technician',
    supervisor: 'Farm Technician',
    owner: 'Farm Owner / Farm Manager',
    operator: 'Operator',
  };
  return labels[role] || role;
}
